// Message rules (06 §Messages; 04 §Base rules), adapted from Work OS `message-writes.ts`:
//   - BlockNote JSON is the body; `body_text` is its plain text (search, previews, events) and
//     `body_markdown` on write is converted here, so agents never emit BlockNote JSON (04);
//   - an edit is the author's, inside the edit window (06: 15 minutes, configurable), and never
//     adds a mention (Work OS `editMessage`: no notification was sent for it);
//   - a delete is a tombstone: the row stays, its body, text and mentions are emptied;
//   - a DM is keyed by its member set (Work OS `dmKeyOf`): the sorted membership ids.

/** One BlockNote block (or inline node): a JSON object. */
export type Block = Record<string, unknown>;

/** 06: "Edit (15 min window configurable)". */
export const DEFAULT_EDIT_WINDOW_MS = 15 * 60_000;

/** May a message written at `createdAt` still be edited at `now`? */
export function withinEditWindow(
  createdAt: Date,
  now: Date,
  windowMs = DEFAULT_EDIT_WINDOW_MS,
): boolean {
  return now.getTime() - createdAt.getTime() <= windowMs;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** The text of inline content: text runs, links' text, mentions' labels. */
function inlineText(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) {
    // A table's content: `{ type: "tableContent", rows: [{ cells: [[inline…], …] }] }`.
    if (isObj(content) && Array.isArray(content.rows))
      return content.rows
        .map((r) =>
          isObj(r) && Array.isArray(r.cells)
            ? r.cells.map((c) => inlineText(isObj(c) ? c.content : c)).join("\t")
            : "",
        )
        .join("\n");
    return "";
  }
  return content
    .map((n) => {
      if (typeof n === "string") return n;
      if (!isObj(n)) return "";
      if (typeof n.text === "string") return n.text;
      if (n.type === "link") return inlineText(n.content);
      // The composer's mention node carries `name` (packages/ui chat composer); `label` is the
      // older spelling. Either way the text keeps "@Name" (AGT-UI-2).
      if (n.type === "mention" && isObj(n.props)) {
        const label = n.props.label ?? n.props.name;
        if (typeof label === "string") return `@${label}`;
      }
      // WIKI-LINKS-GRAPH: a doc link from the [[ picker reads as its title
      if (n.type === "pageLink" && isObj(n.props) && typeof n.props.title === "string")
        return n.props.title;
      return inlineText(n.content);
    })
    .join("");
}

/**
 * The plain text of a BlockNote document: one line per block, children indented under their
 * parent's line, whitespace inside a line kept. Unknown block types contribute their content.
 */
export function bodyTextOf(doc: readonly unknown[]): string {
  const lines: string[] = [];
  const walk = (blocks: readonly unknown[], depth: number) => {
    for (const b of blocks) {
      if (!isObj(b)) continue;
      const text = inlineText(b.content);
      if (text !== "") lines.push(`${"  ".repeat(depth)}${text}`);
      if (Array.isArray(b.children)) walk(b.children, depth + 1);
    }
  };
  walk(doc, 0);
  return lines.join("\n").trim();
}

// Markdown → BlockNote ---------------------------------------------------------------------------

type Styles = { bold?: true; italic?: true; code?: true; strike?: true };
type Inline =
  | { type: "text"; text: string; styles: Styles }
  | { type: "link"; href: string; content: { type: "text"; text: string; styles: Styles }[] };

const INLINE =
  /(\*\*[^*]+\*\*|__[^_]+__|~~[^~]+~~|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*]+\*|_[^_]+_)/g;

/** Inline markdown: **bold**, *italic*, ~~strike~~, `code`, [text](url); the rest is text. */
export function inlineMarkdown(s: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  for (const m of s.matchAll(INLINE)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ type: "text", text: s.slice(last, at), styles: {} });
    const t = m[0];
    if (t.startsWith("**") || t.startsWith("__"))
      out.push({ type: "text", text: t.slice(2, -2), styles: { bold: true } });
    else if (t.startsWith("~~"))
      out.push({ type: "text", text: t.slice(2, -2), styles: { strike: true } });
    else if (t.startsWith("`"))
      out.push({ type: "text", text: t.slice(1, -1), styles: { code: true } });
    else if (t.startsWith("[")) {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(t);
      if (link?.[1] && link[2])
        out.push({
          type: "link",
          href: link[2],
          content: [{ type: "text", text: link[1], styles: {} }],
        });
    } else out.push({ type: "text", text: t.slice(1, -1), styles: { italic: true } });
    last = at + t.length;
  }
  if (last < s.length) out.push({ type: "text", text: s.slice(last), styles: {} });
  return out;
}

const block = (type: string, content: unknown, props: Record<string, unknown> = {}): Block => ({
  type,
  props,
  content,
  children: [],
});

/**
 * A markdown body as BlockNote blocks (04 `body_markdown`): headings (#–###), bullet and numbered
 * list items, quotes, fenced code, and paragraphs (consecutive lines join with a newline).
 * Deliberately small: what agents write in chat, not a CommonMark implementation.
 */
export function markdownToBlocks(md: string): Block[] {
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const out: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length > 0) out.push(block("paragraph", inlineMarkdown(para.join("\n"))));
    para = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    const fence = /^```\s*([\w+-]*)\s*$/.exec(line);
    if (fence) {
      flush();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i] ?? "")) code.push(lines[i++] ?? "");
      out.push(
        block("codeBlock", [{ type: "text", text: code.join("\n"), styles: {} }], {
          language: fence[1] || "text",
        }),
      );
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const quote = /^>\s?(.*)$/.exec(line);
    if (heading?.[1] && heading[2] !== undefined) {
      flush();
      out.push(block("heading", inlineMarkdown(heading[2]), { level: heading[1].length }));
    } else if (bullet?.[1] !== undefined) {
      flush();
      out.push(block("bulletListItem", inlineMarkdown(bullet[1])));
    } else if (numbered?.[1] !== undefined) {
      flush();
      out.push(block("numberedListItem", inlineMarkdown(numbered[1])));
    } else if (quote?.[1] !== undefined) {
      flush();
      out.push(block("quote", inlineMarkdown(quote[1])));
    } else if (line.trim() === "") flush();
    else para.push(line);
  }
  flush();
  return out;
}

// Held post editing (AGT-UI-2) ------------------------------------------------------------------
//
// An approver edits a held agent post block by block, never through a Markdown round trip: only
// a block the approver changed is rebuilt, and every other block (its props, styles, links,
// mentions, children) is passed through as the very same object. A block is editable only when
// its content is plain, unstyled text, which a text field edits exactly; anything else (styled
// text, links, mentions, tables, images) is shown read-only.

/** One block of a held post as the approver sees it. */
export type PostPart = {
  /** `heldPartKey(path)`: the block's index path (children nest). */
  key: string;
  type: string;
  /** Nesting level (0 for top-level blocks). */
  depth: number;
  /** The block's own text (a code block's body verbatim; children are their own parts). */
  text: string;
  /** Its content is plain text only, so a text field edits it exactly. */
  editable: boolean;
};

export const heldPartKey = (path: readonly number[]) => path.join(".");

/** A text node without any style turned on. */
function isPlainText(n: unknown): n is { type: "text"; text: string } {
  if (!isObj(n) || n.type !== "text" || typeof n.text !== "string") return false;
  if (n.styles === undefined) return true;
  return isObj(n.styles) && Object.values(n.styles).every((v) => v === false || v == null);
}

const editableContent = (content: unknown): content is { type: "text"; text: string }[] =>
  Array.isArray(content) && content.every(isPlainText);

/** The post's blocks, depth first, each with its text and whether it can be edited exactly. */
export function heldPostParts(doc: readonly unknown[]): PostPart[] {
  const out: PostPart[] = [];
  const walk = (blocks: readonly unknown[], at: number[]) =>
    blocks.forEach((b, i) => {
      if (!isObj(b)) return;
      const path = [...at, i];
      out.push({
        key: heldPartKey(path),
        type: typeof b.type === "string" ? b.type : "block",
        depth: at.length,
        text: inlineText(b.content),
        editable: editableContent(b.content),
      });
      if (Array.isArray(b.children)) walk(b.children, path);
    });
  walk(doc, []);
  return out;
}

/**
 * The post with `edits` (part key → new text) applied. A block is rebuilt only when it is
 * editable and its text really changed; its content becomes that text (the single node's own
 * fields kept when it had one). Everything else is returned as the same objects, so unedited
 * parts serialize byte for byte as the agent wrote them.
 */
export function editHeldPost(
  doc: readonly unknown[],
  edits: ReadonlyMap<string, string>,
): unknown[] {
  const walk = (blocks: readonly unknown[], at: number[]): unknown[] =>
    blocks.map((b, i) => {
      if (!isObj(b)) return b;
      const path = [...at, i];
      let next: Record<string, unknown> = b;
      const text = edits.get(heldPartKey(path));
      if (text !== undefined && editableContent(b.content) && text !== inlineText(b.content)) {
        const only = b.content.length === 1 ? b.content[0] : undefined;
        const node = only ? { ...only, text } : { type: "text", text, styles: {} };
        next = { ...b, content: text === "" ? [] : [node] };
      }
      if (Array.isArray(b.children) && b.children.length) {
        const before: unknown[] = b.children;
        const children = walk(before, path);
        if (children.some((c, j) => c !== before[j])) next = { ...next, children };
      }
      return next;
    });
  return walk(doc, []);
}

// Mentions -------------------------------------------------------------------------------------

/** `person:{id}`, `seat:{id}`, `team:{id}`, `de:{id}` (06 §Messages). */
export type MentionRef = { kind: "person" | "seat" | "team" | "de"; id: string };

const MENTION = /^(person|seat|team|de):([0-9a-f-]{36})$/;

export function parseMention(ref: string): MentionRef | null {
  const m = MENTION.exec(ref);
  return m?.[1] && m[2] ? { kind: m[1] as MentionRef["kind"], id: m[2] } : null;
}

/** Mention refs once each, in first-seen order, lower-cased ids. */
export function uniqueMentions(refs: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of refs) {
    const k = r.toLowerCase();
    if (parseMention(k) && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

/** An edit keeps the mentions it still names and never adds one (Work OS `editMessage`). */
export function editedMentions(before: readonly string[], after: readonly string[]): string[] {
  const kept = new Set(uniqueMentions(after));
  return before.filter((m) => kept.has(m));
}

// DMs ----------------------------------------------------------------------------------------

/** The DM key of a member set: its distinct membership ids, lower-cased, sorted, joined by `,`. */
export function dmKeyOf(membershipIds: readonly string[]): string {
  return [...new Set(membershipIds.map((m) => m.toLowerCase()))].sort().join(",");
}

/** A DM has two members; a group DM three or more (06: "group DMs may use topics"). */
export function dmKindOf(memberCount: number): "dm" | "group_dm" {
  return memberCount <= 2 ? "dm" : "group_dm";
}
